import React, { useEffect, useState } from "react";
import Select from "react-select";
import ImageWithBasePath from "../../img/imagewithbasebath";
import { Link } from "react-router-dom";
import { Trash2 } from "react-feather";
import PropTypes from 'prop-types';
import { useToast } from '../../../Toaster/ToastContext';
import { apiClient } from "../../../services/apiClient";

const StockTransferModal = ({ editData = null, onUpdate }) => {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedFromBranch, setSelectedFromBranch] = useState(null);
  const [selectedToBranch, setSelectedToBranch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const { showToast } = useToast();

  useEffect(() => {
    if (editData) {
      setSelectedFromBranch({
        value: editData.fromWarehouse.id,
        label: editData.fromWarehouse
      });
      setSelectedToBranch({
        value: editData.toWarehouse.id,
        label: editData.toWarehouse
      });
      // Set selected products if available in editData
      if (editData.products) {
        setSelectedProducts(editData.products.map(product => ({
          id: product.id,
          name: product.name,
          sku: product.sku,
          category: product.category,
          qty: product.quantity
        })));
      }
    } else {
      setSelectedFromBranch(null);
      setSelectedToBranch(null);
      setSelectedProducts([]);
    }
  }, [editData]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/get-all-branch');
        const branchOptions = response.data.map(branch => ({
          value: branch.id,
          label: branch.name
        }));
        setBranches([{ value: "choose", label: "Choose" }, ...branchOptions]);
      } catch (err) {
        console.error("Error fetching branches:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBranches();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      if (selectedFromBranch && selectedFromBranch.value !== "choose") {
        try {
          setLoading(true);
          const response = await apiClient.get('/get-all-products');
          const filteredProducts = response.data.filter(
            product => product.branch_id === selectedFromBranch.value.toString()
          );
          setProducts(filteredProducts);
          const options = filteredProducts.map(product => ({
            value: product.id,
            label: product.name,
            product: product
          }));
          setProductOptions([{ value: "choose", label: "Choose Product" }, ...options]);
        } catch (err) {
          console.error("Error fetching products:", err);
        } finally {
          setLoading(false);
        }
      } else {
        setProducts([]);
        setProductOptions([{ value: "choose", label: "Choose Product" }]);
      }
    };
    fetchProducts();
  }, [selectedFromBranch]);

  const handleFromBranchChange = (selectedOption) => {
    setSelectedFromBranch(selectedOption);
    setSelectedProducts([]);
  };

  const handleToBranchChange = (selectedOption) => {
    setSelectedToBranch(selectedOption);
  };

  const getToBranchOptions = () => {
    if (!selectedFromBranch || selectedFromBranch.value === "choose") {
      return branches;
    }
    return branches.filter(branch => branch.value !== selectedFromBranch.value);
  };

  const handleProductSelect = (selectedOption) => {
    if (!selectedOption || selectedOption.value === "choose") {
      return;
    }
    const selectedProduct = products.find(p => p.id === selectedOption.value);
    if (selectedProduct) {
      const newProduct = {
        id: selectedProduct.id,
        name: typeof selectedProduct.name === 'string' ? selectedProduct.name : selectedProduct.name?.name || 'Unknown',
        sku: typeof selectedProduct.sku === 'string' ? selectedProduct.sku : selectedProduct.sku?.sku || '',
        category: typeof selectedProduct.category === 'string' ? selectedProduct.category : selectedProduct.category?.name || '',
        qty: 1,
        variants: selectedProduct.variants || [],
        variantQuantities: selectedProduct.variants ? selectedProduct.variants.map(v => ({ id: v.id, qty: 0 })) : []
      };
      setSelectedProducts([...selectedProducts, newProduct]);
    }
  };

  const handleVariantQuantityChange = (productId, variantId, newQty, totalProductQty) => {
    setSelectedProducts(selectedProducts.map(product => {
      if (product.id === productId) {
        const otherVariantsTotal = product.variantQuantities
          .filter(vq => vq.id !== variantId)
          .reduce((sum, vq) => sum + (vq.qty || 0), 0);

        const maxAllowedQty = Math.max('', totalProductQty - otherVariantsTotal);

        const validatedQty = Math.min(newQty, maxAllowedQty);
        
        const updatedVariantQuantities = product.variantQuantities.map(vq =>
          vq.id === variantId ? { ...vq, qty: validatedQty } : vq
        );
        
        return { ...product, variantQuantities: updatedVariantQuantities };
      }
      return product;
    }));
  };
  
  const handleQuantityChange = (id, newQty) => {
    setSelectedProducts(selectedProducts.map(product => 
      product.id === id ? { ...product, qty: newQty } : product
    ));
  };

  const handleRemoveProduct = (id) => {
    setSelectedProducts(selectedProducts.filter(product => product.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFromBranch || !selectedToBranch || selectedProducts.length === 0) {
      showToast("Please fill all required fields", 'error', 'Stock Transfer Operation!');
      return;
    }

    try {
      const submissionData = {
        from_branch_id: selectedFromBranch.value,
        to_branch_id: selectedToBranch.value,
        products: selectedProducts.map(product => ({
          product_id: product.id,
          name: product.name,
          sku: product.sku,
          category: product.category,
          quantity: product.qty,
          variant_quantities: product.variantQuantities
        }))
      };
      await apiClient.post('/transfer-quantity', submissionData);
      // Reset form
      setSelectedFromBranch(null);
      setSelectedToBranch(null);
      setSelectedProducts([]);
     
      // Close modal
      document.querySelector('[data-bs-dismiss="modal"]').click();
      if (onUpdate) {
        onUpdate();
      }
      showToast("Stock transfer created successfully", 'success', 'Stock Transfer Operation!');
    // } catch (error) {
    //   setSelectedFromBranch(null);
    //   setSelectedToBranch(null);
    //   setSelectedProducts([]);
    //   document.querySelector('[data-bs-dismiss="modal"]').click();
    //   showToast(error.message || "Failed to create stock transfer", 'error', 'Stock Transfer Operation!');
    // }
    } catch (error) {
      setSelectedFromBranch(null);
      setSelectedToBranch(null);
      setSelectedProducts([]);
      document.querySelector('[data-bs-dismiss="modal"]').click();

      // Extract the error message from the nested structure
      let errorMessage = "Failed to create stock transfer"; // Default message
      if (error.message && error.message['products.0.quantity'] && error.message['products.0.quantity'][0]) {
        errorMessage = error.message['products.0.quantity'][0];
      }

      showToast(errorMessage, 'error', 'Stock Transfer Operation!');
    }
  };

  return (
    <div>
      {/* Add Stock */}
      <div className="modal fade" id="add-units">
        <div className="modal-dialog modal-dialog-centered stock-adjust-modal">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-0 custom-modal-header">
                  <div className="page-title">
                    <h4>{editData ? 'Edit Transfer' : 'Add Transfer'}</h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body custom-modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Branch From</label>
                          <Select
                            className="select"
                            options={branches}
                            isLoading={loading}
                            onChange={handleFromBranchChange}
                            value={selectedFromBranch}
                          />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Branch To</label>
                          <Select
                            className="select"
                            options={getToBranchOptions()}
                            isLoading={loading}
                            isDisabled={loading || !selectedFromBranch}
                            onChange={handleToBranchChange}
                            value={selectedToBranch}
                          />
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="input-blocks mb-3">
                          <label>Product</label>
                          <Select
                            className="select"
                            options={productOptions}
                            onChange={handleProductSelect}
                            isLoading={loading}
                            isDisabled={loading || !selectedFromBranch || selectedFromBranch.value === "choose"}
                          />
                        </div>
                      </div>
                      {selectedProducts.length > 0 && (
                        <div className="col-lg-12">
                          <div className="modal-body-table">
                            <div className="table-responsive">
                              <table className="table datanew">
                                <thead>
                                  <tr>
                                    <th>Product</th>
                                    <th>SKU</th>
                                    <th>Category</th>
                                    <th>Qty</th>
                                    <th className="no-sort">Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedProducts.map((product) => (
                                    <React.Fragment key={product.id}>
                                      <tr>
                                        <td>
                                          <div className="productimgname">
                                            <Link to="#" className="product-img stock-img">
                                              <ImageWithBasePath src="assets/img/products/stock-img-02.png" alt="product" />
                                            </Link>
                                            <Link to="#">
                                              {typeof product.name === 'string' ? product.name : product.name?.name || 'Unknown'}
                                            </Link>
                                          </div>
                                        </td>
                                        <td>{typeof product.sku === 'string' ? product.sku : product.sku?.sku || ''}</td>
                                        <td>{typeof product.category === 'string' ? product.category : product.category?.name || ''}</td>
                                        <td>
                                          <div className="product-quantity">
                                            <span className="quantity-btn"
                                              onClick={() => handleQuantityChange(product.id, Math.max(1, product.qty - 1))}
                                            >
                                              <i className="feather-minus-circle" />
                                            </span>
                                            <input type="text" className="quntity-input" value={product.qty}
                                              onChange={(e) => {
                                                const newQty = parseInt(e.target.value) || '';
                                                handleQuantityChange(product.id, newQty);
                                              }}
                                            />
                                            <span className="quantity-btn"
                                              onClick={() => handleQuantityChange(product.id, product.qty + 1)}
                                            >
                                              <i className="feather-plus-circle" />
                                            </span>
                                          </div>
                                        </td>
                                        <td className="action-table-data">
                                          <div className="edit-delete-action">
                                            <Link className="confirm-text p-2" to="#"
                                              onClick={() => handleRemoveProduct(product.id)}
                                            >
                                              <Trash2 className="feather-trash-2" />
                                            </Link>
                                          </div>
                                        </td>
                                      </tr>
                                      {product.variants && product.variants.length > 0 && (
                                        <tr className="variant-row">
                                          <td colSpan="5">
                                            <div className="variants-container">
                                              <h5>Variants</h5>
                                              <div className="variants-list">
                                                {product.variants.map((variant) => (
                                                  <div key={variant.id} className="variant-item">
                                                    <span>{variant.name} ({variant.variant_value})</span>
                                                    <div className="product-quantity">
                                                      <span className="quantity-btn"
                                                        onClick={() => {
                                                          const currentQty = product.variantQuantities.find(vq => vq.id === variant.id)?.qty || 0;
                                                          handleVariantQuantityChange(product.id, variant.id, Math.max(0, currentQty - 1), product.qty);
                                                        }}
                                                      >
                                                        <i className="feather-minus-circle" />
                                                      </span>
                                                      <input type="text" className="quntity-input"
                                                        value={product.variantQuantities.find(vq => vq.id === variant.id)?.qty || ''}
                                                        onChange={(e) => {
                                                          const newQty = parseInt(e.target.value) || 0;
                                                          handleVariantQuantityChange(product.id, variant.id, newQty, product.qty);
                                                        }}
                                                      />
                      <span
                        className="quantity-btn"
                        onClick={() => {
                          const currentQty = product.variantQuantities.find(vq => vq.id === variant.id)?.qty || 0;
                          handleVariantQuantityChange(product.id, variant.id, currentQty + 1, product.qty);
                        }}
                      >
                        <i className="feather-plus-circle" />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  ))}
</tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="modal-footer-btn">
                      <button
                        type="button"
                        className="btn btn-cancel me-2"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-submit">
                        Create
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Add Stock */}
      {/* Edit Stock */}
      {/* <div className="modal fade" id="edit-units">
        <div className="modal-dialog modal-dialog-centered stock-adjust-modal">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-0 custom-modal-header">
                  <div className="page-title">
                    <h4>Edit Transfer</h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body custom-modal-body">
                  <form>
                    <div className="input-blocks search-form">
                      <label>Product</label>
                      <input
                        type="text"
                        className="form-control"
                        defaultValue="Nike Jordan"
                      />
                      <i
                        data-feather="search"
                        className="feather-search custom-search"
                      />
                    </div>
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Warehouse From</label>
                          <Select className="select" options={optionsChoose} />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Warehouse To</label>
                          <Select
                            className="select"
                            options={optionsSelosyLogerro}
                          />
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="input-blocks">
                          <label>Reference No</label>
                          <input
                            type="text"
                            className="form-control"
                            defaultValue={32434545}
                          />
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="input-blocks search-form mb-3">
                          <label>Product</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Select Product"
                            defaultValue="Nike Jordan"
                          />
                          <i
                            data-feather="search"
                            className="feather-search custom-search"
                          />
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="modal-body-table">
                          <div className="table-responsive">
                            <table className="table  datanew">
                              <thead>
                                <tr>
                                  <th>Product</th>
                                  <th>SKU</th>
                                  <th>Category</th>
                                  <th>Qty</th>
                                  <th className="no-sort">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>
                                    <div className="productimgname">
                                      <Link
                                        to="#"
                                        className="product-img stock-img"
                                      >
                                        <ImageWithBasePath
                                          src="assets/img/products/stock-img-02.png"
                                          alt="product"
                                        />
                                      </Link>
                                      <Link to="#">Nike Jordan</Link>
                                    </div>
                                  </td>
                                  <td>PT002</td>
                                  <td>Nike</td>
                                  <td>
                                    <div className="product-quantity">
                                      <span className="quantity-btn">
                                        <i
                                          data-feather="minus-circle"
                                          className="feather-search"
                                        />
                                      </span>
                                      <input
                                        type="text"
                                        className="quntity-input"
                                        defaultValue={2}
                                      />
                                      <span className="quantity-btn">
                                        +
                                        <i
                                          data-feather="plus-circle"
                                          className="plus-circle"
                                        />
                                      </span>
                                    </div>
                                  </td>
                                  <td className="action-table-data">
                                    <div className="edit-delete-action">
                                      <Link
                                        className="me-2 p-2"
                                        to="#"
                                        data-bs-toggle="modal"
                                        data-bs-target="#edit-units"
                                      >
                                        <i
                                          data-feather="edit"
                                          className="feather-edit"
                                        />
                                      </Link>
                                      <Link className="confirm-text p-2" to="#">
                                        <i
                                          data-feather="trash-2"
                                          className="feather-trash-2"
                                        />
                                      </Link>
                                    </div>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-12">
                        <div className="input-blocks search-form mb-0">
                          <label>Notes</label>
                          <textarea
                            className="form-control"
                            defaultValue={
                              "The Jordan brand is owned by Nike (owned by the Knight family), as, at the time, the company was building its strategy to work with athletes to launch shows that could inspire consumers.Although Jordan preferred Converse and Adidas, they simply could not match the offer Nike made. "
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer-btn">
                      <button
                        type="button"
                        className="btn btn-cancel me-2"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-submit">
                        Save Changes
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div> */}
      {/* /Edit Stock */}
      {/* Import Transfer */}
      {/* <div className="modal fade" id="view-notes">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-0 custom-modal-header">
                  <div className="page-title">
                    <h4>Import Transfer</h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
              </div>
              <div className="modal-body custom-modal-body">
                <form>
                  <div className="row">
                    <div className="col-lg-4 col-sm-6 col-12">
                      <div className="input-blocks">
                        <label>From</label>
                        <Select
                          className="select"
                          options={optionsStore1Store2}
                        />
                      </div>
                    </div>
                    <div className="col-lg-4 col-sm-6 col-12">
                      <div className="input-blocks">
                        <label>To</label>
                        <Select
                          className="select"
                          options={optionsStore1Store2}
                        />
                      </div>
                    </div>
                    <div className="col-lg-4 col-sm-6 col-12">
                      <div className="input-blocks">
                        <label>Satus</label>
                        <Select
                          className="select"
                          options={optionsSentPending}
                        />
                      </div>
                    </div>
                    <div className="col-lg-12 col-sm-6 col-12">
                      <div className="row">
                        <div>
                          <div className="modal-footer-btn download-file">
                            <Link to="#" className="btn btn-submit">
                              Download Sample File
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-12">
                      <div className="input-blocks image-upload-down">
                        <label> Upload CSV File</label>
                        <div className="image-upload download">
                          <input type="file" />
                          <div className="image-uploads">
                            <ImageWithBasePath
                              src="assets/img/download-img.png"
                              alt="img"
                            />
                            <h4>
                              Drag and drop a <span>file to upload</span>
                            </h4>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-12 col-sm-6 col-12">
                      <div className="mb-3">
                        <label className="form-label">Shipping</label>
                        <input type="text" className="form-control" />
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="mb-3 summer-description-box transfer">
                      <label className="form-label">Description</label>
                      <div id="summernote3"></div>
                      <p>Maximum 60 Characters</p>
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="modal-footer-btn">
                      <button
                        type="button"
                        className="btn btn-cancel me-2"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-submit">
                        Submit
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div> */}
      {/* /Import Transfer */}
    </div>
  );
};

StockTransferModal.propTypes = {
  editData: PropTypes.shape({
    fromWarehouse: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string
    }),
    toWarehouse: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string
    }),
    products: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        sku: PropTypes.string,
        category: PropTypes.string,
        quantity: PropTypes.number
      })
    )
  }),
  onUpdate: PropTypes.func,
};

export default StockTransferModal;
